import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { formatNumber } from "../utils/formatNumber";

function ItemTableModal({ open, onClose, item, onOpenExtraPrice, isSale = false }) {
  if (!item) return null;

  // รวมข้อมูลสรุป
  const totalWeight = item.details.reduce((sum, d) => sum + d.usedWeight, 0);
  const totalPrice = item.details.reduce((sum, d) => sum + d.totalPrice, 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        style: { borderRadius: "16px" },
        className: "shadow-2xl overflow-hidden"
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
        <div>
           <h2 className="text-xl font-black text-slate-800 tracking-tight">
            {item.productName}
           </h2>
           <p className="text-sm text-slate-500 font-medium mt-0.5">
             {isSale ? "รายละเอียดการขายสินค้า" : "รายละเอียดการรับซื้อสินค้า"}
           </p>
        </div>
        <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all"
        >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
      </div>

      <DialogContent className="!p-0">
        <div className="overflow-x-auto">
        <Table>
          <TableHead>
            <TableRow className="bg-gray-50 border-b border-gray-100">
              <TableCell className="!py-4 !px-6 !text-xs !font-bold !text-slate-500 !uppercase !tracking-wider">ประเภทราคา</TableCell>
              <TableCell align="right" className="!py-4 !px-6 !text-xs !font-bold !text-slate-500 !uppercase !tracking-wider">{isSale ? "น้ำหนักขายจริง" : "น้ำหนักซื้อจริง"}</TableCell>
              <TableCell align="right" className="!py-4 !px-6 !text-xs !font-bold !text-slate-500 !uppercase !tracking-wider">{isSale ? "ราคาขาย" : "ราคาซื้อ"}</TableCell>
              <TableCell align="right" className="!py-4 !px-6 !text-xs !font-bold !text-slate-500 !uppercase !tracking-wider">ราคารวม</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {item.details.map((detail, idx) => (
              <TableRow key={idx} className="hover:bg-blue-50/30 transition-colors border-b border-gray-50 last:border-0">
                <TableCell className="!py-4 !px-6 !text-sm !font-bold !text-slate-700">
                  {(() => {
                    switch (detail.priceSource) {
                      case "po":
                        return (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 text-xs font-bold border border-purple-100">
                                PO #{detail?.poInfo?.poNumber ? detail.poInfo.poNumber.split("-").pop() : "ไม่ระบุ"}
                            </span>
                        );
                      case "base":
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-50 text-slate-600 text-xs font-bold border border-slate-200">
                            ราคาพื้นฐาน
                          </span>
                        );
                      case "group":
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-100">
                            {isSale ? "ราคากำหนดเอง" : "ราคากลุ่ม"}
                          </span>
                        );
                      case "custom":
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-bold border border-amber-100">
                            ราคากำหนดเอง
                          </span>
                        );
                      case "insurance":
                        return <span className="text-emerald-700">ประกันราคา #{detail.lockInfo?.id}</span>;
                      case "so":
                        return (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-50 text-orange-700 text-xs font-bold border border-orange-100">
                                SO #{String(detail?.soInfo?.saleOrder?.soNumber ?? detail?.soInfo?.soNumber ?? String(detail?.soInfo?.saleOrderId ?? "").padStart(3, "0")).split("-").pop()}
                            </span>
                        );
                      default:
                        return (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-50 text-gray-600 text-xs font-bold border border-gray-200">
                                ราคาปกติ
                            </span>
                        );
                    }
                  })()}
                  {((isSale && detail.priceSource === "group") ||
                    (!isSale && detail.priceSource !== "po")) &&
                    typeof onOpenExtraPrice === "function" && (
                    <div className={isSale ? "inline-flex ml-2" : "mt-2"}>
                      <button
                        type="button"
                        onClick={() => onOpenExtraPrice(item)}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-black text-amber-700 hover:bg-amber-100 transition-colors"
                      >
                        เลือกราคา
                      </button>
                    </div>
                  )}
                </TableCell>
                <TableCell align="right" className="!py-4 !px-6 !text-sm !font-bold !text-slate-700">
                  {formatNumber(detail.usedWeight)}
                </TableCell>
                <TableCell align="right" className="!py-4 !px-6 !text-sm !font-bold !text-slate-600">
                  {(() => {
                    switch (detail.priceSource) {
                      case "po":
                        return formatNumber(detail.poPricePerUnit);
                      case "base":
                        // pricePerUnit คือราคาที่บันทึกไว้จริงของแถวส่วนเกิน
                        return formatNumber(detail.pricePerUnit ?? detail.basePrice);
                      case "group":
                        return formatNumber(detail.pricePerUnit ?? detail.groupPrice);
                      case "custom":
                        return formatNumber(detail.pricePerUnit);
                      case "so":
                        return formatNumber(detail.PricePerUnit);
                      default:
                        return formatNumber(detail.pricePerUnit);
                    }
                  })()}
                </TableCell>
                <TableCell align="right" className="!py-4 !px-6 !text-sm !font-black !text-slate-800">
                  {formatNumber(detail.totalPrice)}
                </TableCell>
              </TableRow>
            ))}

            {/* แถวสรุป */}
            <TableRow className="bg-slate-50/80">
              <TableCell className="!py-4 !px-6 !text-sm !font-black !text-slate-800">รวม</TableCell>
              <TableCell align="right" className="!py-4 !px-6 !text-sm !font-black !text-emerald-600">
                {formatNumber(totalWeight)}
              </TableCell>
              <TableCell></TableCell>
              <TableCell align="right" className="!py-4 !px-6 !text-base !font-black !text-blue-600">
                {formatNumber(totalPrice)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        </div>
      </DialogContent>
      {/* <DialogActions>
        <Button onClick={onClose}>ปิด</Button>
      </DialogActions> */}
    </Dialog>
  );
}

export default ItemTableModal;
